import React, { useEffect, useState } from 'react';
import { MutationFunction, useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useSnackbar } from 'notistack';
import { OfflineNotice, useIsOffline } from '../../offline';
import TypeLeadTimeEditor from './TypeLeadTimeEditor';
import AppointmentRemindersCard, { InstructionRow } from './AppointmentRemindersCard';
import {
  ReminderSettings,
  reminderService,
  RecipientPolicy,
  TypeLeadTimes,
  UpdateReminderSettingsPayload,
} from '../../services/reminderService';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { INSTANT_FORMATS } from '../../utils/dateUtils';

/**
 * 0-23 -> "12 AM" ... "11 PM". This labels an HOUR NUMBER, not a moment:
 * `sendHourLocal` is an integer the scheduler resolves in the CLINIC's zone
 * (`reminder-scheduler.service.ts` clinicLocalHour), so there is no instant here
 * to render and nothing to zone. The old spelling built a throwaway
 * `new Date(2026, 0, 1, h)` only to borrow toLocaleTimeString — the
 * DATE-KINDS.md 2.6 "non-bug" shape: harmless, but visually identical to the
 * real previous-day trap. Arithmetic cannot be mistaken for either.
 */
export function hourLabel(h: number): string {
  return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;
}

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: hourLabel(h),
}));

/** Section shell: one outlined card per part, with its on/off switch (if any) beside the title. */
const Section: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
  id?: string;
}> = ({ title, subtitle, action, children, id }) => (
  <Card variant="outlined" sx={{ mb: 2 }} id={id}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="subtitle1">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: children ? 2 : 0 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const SubHeading: React.FC<{ children: React.ReactNode; first?: boolean }> = ({ children, first }) => (
  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, mt: first ? 0 : 2.5 }}>
    {children}
  </Typography>
);

/** Everything the page's one Save sends. */
interface Form {
  enabled: boolean;
  smsEnabled: boolean;
  sendHourLocal: number;
  replyToEmail: string;
  backfillCutoffDays: number;
  recipientPolicy: RecipientPolicy;
  // Per-type notice timing (ADR-0020/0021), RESOLVED by the server (stored config over the defaults).
  typeLeadTimes: TypeLeadTimes;
  // Appointment reminders (ADR-0046).
  apptEnabled: boolean;
  apptLeadDays: number[];
  apptInstructions: InstructionRow[];
}

const EMPTY_FORM: Form = {
  enabled: false,
  smsEnabled: false,
  sendHourLocal: 9,
  replyToEmail: '',
  backfillCutoffDays: 60,
  recipientPolicy: 'ALL_OWNERS',
  typeLeadTimes: {},
  apptEnabled: false,
  apptLeadDays: [3],
  apptInstructions: [],
};

function formFrom(settings: ReminderSettings): Form {
  return {
    enabled: settings.enabled,
    smsEnabled: settings.smsEnabled ?? false,
    sendHourLocal: settings.sendHourLocal,
    replyToEmail: settings.replyToEmail ?? '',
    backfillCutoffDays: settings.backfillCutoffDays,
    recipientPolicy: settings.recipientPolicy ?? 'ALL_OWNERS',
    // Deep copy: the editor patches per-type entries, and mutating the react-query cache object in
    // place would desync every other consumer. The enabled flag survives (stored only when false).
    typeLeadTimes: Object.fromEntries(
      Object.entries(settings.typeLeadTimes ?? {}).map(([k, v]) => [
        k,
        {
          preDays: [...(v.preDays ?? [])],
          overdueDays: [...(v.overdueDays ?? [])],
          ...(v.enabled === false ? { enabled: false as const } : {}),
        },
      ]),
    ),
    apptEnabled: settings.appointmentRemindersEnabled ?? false,
    apptLeadDays: [...(settings.appointmentLeadDays ?? [3])],
    // Keys come back lowercased; capitalise the first letter for the label.
    apptInstructions: Object.entries(settings.appointmentInstructions ?? {}).map(([type, text]) => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      text,
    })),
  };
}

/** A form in one comparable shape, for "unsaved changes": day lists sorted, so a reorder is no change. */
function formKey(f: Form): string {
  const sorted = (days: number[]) => [...days].sort((a, b) => b - a);
  return JSON.stringify([
    f.enabled,
    f.smsEnabled,
    f.sendHourLocal,
    f.replyToEmail.trim(),
    f.backfillCutoffDays,
    f.recipientPolicy,
    Object.keys(f.typeLeadTimes)
      .sort()
      .map((k) => {
        const t = f.typeLeadTimes[k];
        return [k, sorted(t.preDays), sorted(t.overdueDays), t.enabled === false];
      }),
    f.apptEnabled,
    sorted(f.apptLeadDays),
    f.apptInstructions.map((r) => [r.type, r.text]),
  ]);
}

/**
 * Client Reminders (Settings): appointment reminders, care reminders, then the email settings both
 * use, with a Save bar while anything is unsaved (Ali, 2026-10-22: the old page mixed them). OWNER and
 * ADMIN reach it through the nav gate; the backend PUT enforces the same roles. Online only (INV-19).
 */
const RemindersSettings: React.FC = () => {
  const isOffline = useIsOffline();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: settings } = useQuery(
    ['reminder-settings'],
    () => reminderService.getSettings(),
    { enabled: !isOffline },
  );
  // Care reminders due now or soon, for the status line.
  const { data: dueRows } = useQuery(
    ['reminders', 'due-list'],
    () => reminderService.getDueList(),
    { enabled: !isOffline },
  );
  const dueCount = (dueRows ?? []).filter(
    (r) => !r.declinedAt && (r.status === 'PENDING' || r.status === 'OVERDUE'),
  ).length;

  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const patch = (next: Partial<Form>) => setForm((f) => ({ ...f, ...next }));
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);
  const [confirmApptOpen, setConfirmApptOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewKind, setPreviewKind] = useState<'care' | 'appointment'>('care');

  // Hydrate from query data in an effect: react-query v3 onSuccess re-fires on cache hits and wiped
  // in-progress edits elsewhere in Settings.
  useEffect(() => {
    if (settings) setForm(formFrom(settings));
  }, [settings]);

  const dirty = !!settings && formKey(form) !== formKey(formFrom(settings));

  const saveMutation = useMutation(
    (payload: UpdateReminderSettingsPayload) => reminderService.updateSettings(payload),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['reminder-settings']);
        queryClient.invalidateQueries(['reminders', 'due-list']);
        if (result.backfill) {
          enqueueSnackbar(
            `Reminders turned on. ${result.backfill.created} reminder${result.backfill.created === 1 ? '' : 's'} created from existing records.`,
            { variant: 'success' },
          );
        } else {
          enqueueSnackbar('Reminder settings saved', { variant: 'success' });
        }
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message || 'Failed to save reminder settings', {
          variant: 'error',
        });
      },
    },
  );

  // Typed as MutationFunction so v3 overload resolution can't mistake a
  // zero-arg fn for a MutationKey.
  const sendTestFn: MutationFunction<{ sent: boolean; to: string }, void> = () =>
    reminderService.sendTestEmail();
  const testSendMutation = useMutation<{ sent: boolean; to: string }, any, void>(
    sendTestFn,
    {
      onSuccess: (r) => {
        enqueueSnackbar(`Test email sent to ${r.to}`, { variant: 'success' });
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message || 'Failed to send the test email', {
          variant: 'error',
        });
      },
    },
  );

  const { data: preview, isLoading: previewLoading } = useQuery(
    ['reminder-email-preview', previewKind],
    () => reminderService.getEmailPreview(previewKind === 'appointment' ? 'appointment' : undefined),
    { enabled: previewOpen && !isOffline },
  );

  const save = (overrides: { enabled?: boolean; appointmentRemindersEnabled?: boolean } = {}) => {
    saveMutation.mutate({
      enabled: overrides.enabled ?? form.enabled,
      smsEnabled: form.smsEnabled,
      sendHourLocal: form.sendHourLocal,
      // '' explicitly CLEARS the reply-to (backend maps '' → null).
      replyToEmail: form.replyToEmail.trim(),
      backfillCutoffDays: form.backfillCutoffDays,
      recipientPolicy: form.recipientPolicy,
      typeLeadTimes: form.typeLeadTimes,
      appointmentRemindersEnabled: overrides.appointmentRemindersEnabled ?? form.apptEnabled,
      appointmentLeadDays: form.apptLeadDays,
      appointmentInstructions: Object.fromEntries(form.apptInstructions.map((r) => [r.type, r.text])),
    });
  };

  const onToggleEnabled = (next: boolean) => {
    if (next && !settings?.enabled) {
      setConfirmEnableOpen(true); // emails to real clients — confirm first
    } else {
      patch({ enabled: next });
    }
  };

  const confirmEnable = () => {
    setConfirmEnableOpen(false);
    patch({ enabled: true });
    save({ enabled: true });
  };

  const onToggleAppointments = (next: boolean) => {
    if (next && !settings?.appointmentRemindersEnabled) {
      setConfirmApptOpen(true); // emails to real clients: confirm first
    } else {
      patch({ apptEnabled: next });
    }
  };

  const confirmAppointments = () => {
    setConfirmApptOpen(false);
    patch({ apptEnabled: true });
    save({ appointmentRemindersEnabled: true });
  };

  const openPreview = (kind: 'care' | 'appointment') => {
    setPreviewKind(kind);
    setPreviewOpen(true);
  };

  const saving = saveMutation.isLoading;
  const sendHourLabel = HOURS[form.sendHourLocal]?.label ?? `${form.sendHourLocal}:00`;

  // ClinicReminderSettings.lastRunAt / .lastSmsRunAt are INSTANTs — stamped
  // `new Date()` by the scheduler (DATE-KINDS.md 1.3, "Messaging"). The clinic
  // zone is the only right one to show them in: the sentence beside them already
  // promises "runs daily after <hour> clinic time", so a browser-zone stamp made
  // the run look hours off its own schedule for remote or relief staff.
  const formatInstant = useFormatInstant();

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Client Reminders
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Email clients before their booked appointments and when care comes due. Clients can
        unsubscribe from any reminder email with one click.
      </Typography>

      <OfflineNotice>Reminder settings need an internet connection.</OfflineNotice>

      <AppointmentRemindersCard
        enabled={form.apptEnabled}
        onToggle={onToggleAppointments}
        leadDays={form.apptLeadDays}
        onLeadDaysChange={(apptLeadDays) => patch({ apptLeadDays })}
        instructions={form.apptInstructions}
        onInstructionsChange={(apptInstructions) => patch({ apptInstructions })}
        // Unknown until settings load: no warning flash, and the card stays disabled meanwhile.
        clinicTimezoneSet={settings?.clinicTimezoneSet ?? true}
        sendHourLabel={sendHourLabel}
        disabled={isOffline || saving || !settings}
        onPreview={() => openPreview('appointment')}
      />

      {/* ── Care reminders ─────────────────────────────────────────────── */}
      <Section
        id="care-reminders-settings"
        title="Care reminders"
        subtitle="Vaccines, Coggins tests, dentals, rechecks and other care coming due, from recorded vaccinations and the reminders on each horse's chart. One email per client per day."
        action={
          <Switch
            checked={form.enabled}
            disabled={isOffline || saving || !settings}
            onChange={(e) => onToggleEnabled(e.target.checked)}
            inputProps={{ 'aria-label': 'Care reminders on/off' }}
          />
        }
      >
        {settings?.lastRunError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The last run reported an error: {settings.lastRunError}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          {settings?.enabled
            ? `Sending. Runs daily after ${sendHourLabel} clinic time.`
            : `Off. When on, runs daily after ${sendHourLabel} clinic time.`}
          {settings?.lastRunAt &&
            ` Last run ${formatInstant(
              settings.lastRunAt,
              INSTANT_FORMATS.dateTimeNumeric,
            )}.`}
          {dueRows !== undefined &&
            ` ${dueCount} reminder${dueCount === 1 ? '' : 's'} currently due or coming due.`}
        </Typography>

        <SubHeading>When to send</SubHeading>
        <TypeLeadTimeEditor
          value={form.typeLeadTimes}
          onChange={(typeLeadTimes) => patch({ typeLeadTimes })}
          disabled={isOffline}
        />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Switch a type off to stop reminding for it. Changing timing affects future reminders only:
          a client already emailed at a stage is never emailed again for it.
        </Typography>

        {/* SMS (ADR-0022). Visibility is env-driven: no TWILIO_* envs = smsConfigured false = a plain
            Coming soon line (prod stays dark until carrier registration clears; staging is live). */}
        <Stack id="sms-reminders-settings" direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Text messages
          </Typography>
          {settings?.smsConfigured ? (
            <Switch
              checked={form.smsEnabled}
              disabled={isOffline || saving}
              onChange={(e) => patch({ smsEnabled: e.target.checked })}
              inputProps={{ 'aria-label': 'Text message reminders on/off' }}
            />
          ) : (
            <Chip size="small" label="Coming soon" />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {settings?.smsConfigured
            ? 'The final before-due reminder and overdue reminders also go by text, only to clients whose consent is recorded on their profile. Sent between 10 AM and 8 PM (from noon on Sundays); every text includes reply-STOP opt-out.'
            : 'Clients with recorded consent will also get the final before-due reminder and overdue reminders by text.'}
        </Typography>
        {settings?.smsConfigured && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            Record consent on the client profile (&quot;Client agreed to receive text reminders&quot;):
            texting requires it by law, and the notification toggles alone are not consent.
            {settings?.lastSmsRunAt &&
              ` Last text run ${formatInstant(
                settings.lastSmsRunAt,
                INSTANT_FORMATS.dateTimeNumeric,
              )}.`}
          </Typography>
        )}

        <Accordion
          disableGutters
          elevation={0}
          sx={{ mt: 2.5, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Advanced
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              type="number"
              label="Skip items overdue more than (days)"
              helperText="Older overdue items are never emailed automatically; they stay on the due list for manual outreach. A notice after due set later than this can never send, so that combination is refused."
              value={form.backfillCutoffDays}
              onChange={(e) =>
                patch({ backfillCutoffDays: Math.max(0, Math.min(365, Number(e.target.value) || 0)) })
              }
              sx={{ maxWidth: 420 }}
              fullWidth
              disabled={isOffline}
            />
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => openPreview('care')}
            disabled={isOffline}
          >
            Preview email
          </Button>
          <Button
            size="small"
            startIcon={<MailOutlineIcon />}
            onClick={() => testSendMutation.mutate()}
            disabled={isOffline || testSendMutation.isLoading}
          >
            Email me a test
          </Button>
        </Stack>
      </Section>

      {/* ── Email settings, shared by both kinds ───────────────────────── */}
      <Section
        id="reminder-email-settings"
        title="Email settings"
        subtitle="For both appointment and care reminders."
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            label="Send time (clinic time)"
            value={form.sendHourLocal}
            onChange={(e) => patch({ sendHourLocal: Number(e.target.value) })}
            sx={{ minWidth: 180 }}
            disabled={isOffline}
          >
            {HOURS.map((h) => (
              <MenuItem key={h.value} value={h.value}>
                {h.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reply-to email"
            placeholder="Defaults to the practice email"
            value={form.replyToEmail}
            onChange={(e) => patch({ replyToEmail: e.target.value })}
            sx={{ minWidth: 260 }}
            disabled={isOffline}
          />
          <TextField
            select
            label="Send emails to"
            value={form.recipientPolicy}
            onChange={(e) => patch({ recipientPolicy: e.target.value as RecipientPolicy })}
            sx={{ minWidth: 260 }}
            disabled={isOffline}
            helperText={
              form.recipientPolicy === 'ALL_OWNERS'
                ? 'When a horse has more than one owner, each gets their own email.'
                : 'When a horse has more than one owner, only the primary owner (largest share) is emailed, for racing and syndicate clients.'
            }
          >
            <MenuItem value="ALL_OWNERS">All linked owners</MenuItem>
            <MenuItem value="PRIMARY_ONLY">Primary owner only</MenuItem>
          </TextField>
        </Box>
      </Section>

      {/* ── Save bar: only while something is unsaved ──────────────────── */}
      {dirty && (
        <Paper
          elevation={6}
          role="region"
          aria-label="Unsaved changes"
          sx={{ position: 'sticky', bottom: 16, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderRadius: 2 }}
        >
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
            You have unsaved changes
          </Typography>
          <Button onClick={() => settings && setForm(formFrom(settings))} disabled={saving}>
            Discard
          </Button>
          <Button variant="contained" onClick={() => save()} disabled={isOffline || saving}>
            Save
          </Button>
        </Paper>
      )}

      {/* ── Enable confirm ─────────────────────────────────────────────── */}
      <Dialog open={confirmEnableOpen} onClose={() => setConfirmEnableOpen(false)}>
        <DialogTitle>Start sending reminder emails?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Clients with due or coming-due care will begin receiving emails at the next send time
            ({sendHourLabel} clinic time). Reminders will also be generated from your existing
            vaccination records with a next-due date. Clients can unsubscribe with one click, and
            you can decline outreach per item on the horse chart.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEnableOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmEnable}>
            Enable reminders
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmApptOpen} onClose={() => setConfirmApptOpen(false)}>
        <DialogTitle>Start sending appointment reminders?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reminders start with the next run: right away if it is between {sendHourLabel} and 8 PM
            clinic time, otherwise at {sendHourLabel}. Clients with a visit{' '}
            {Math.max(...form.apptLeadDays, 0) === 0
              ? 'today'
              : `in the next ${Math.max(...form.apptLeadDays, 0)} days`}{' '}
            are included. Clients can unsubscribe with one click.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApptOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmAppointments}>
            Turn on appointment reminders
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Email preview ──────────────────────────────────────────────── */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          Email preview
          {preview?.subject && (
            <Typography variant="body2" color="text.secondary">
              Subject: {preview.subject}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {previewLoading || !preview ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Building preview…
            </Typography>
          ) : (
            // Sandboxed: renders the exact HTML the scheduler would send, with
            // sample horses — scripts and navigation are inert.
            <Box
              component="iframe"
              sandbox=""
              srcDoc={preview.html}
              title={previewKind === 'appointment' ? 'Appointment reminder preview' : 'Reminder email preview'}
              sx={{ width: '100%', height: 520, border: '1px solid', borderColor: 'divider' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemindersSettings;
